/* Tide stations: the state of the tide at a place, for drawing on a chart.
 *
 * Distinct from `$lib/tidegauge`, which answers one very specific anchoring
 * question — how much water will be under ME tonight — using the live series
 * and the boat's own sounder. This is the chart-wide view: 158 stations, a year
 * of high and low water, shipped with the app so it works with the radio off.
 *
 * ═══ THE DATUM DOES NOT MATTER, AND THAT IS DELIBERATE ═══
 *
 * Every station is published on its own datum. Nothing here ever uses an
 * absolute height — only where the tide sits BETWEEN this cycle's low and high,
 * and which way it is going. Both are differences, so the datum cancels, and no
 * one has to reconcile chart datum with anything. It is the same reasoning the
 * anchor gauge is built on.
 *
 * ═══ WHY A COSINE ═══
 *
 * Only the HW/LW events are shipped — the 10-minute series would be 30 MB. A
 * tide between high and low water is very close to a half-cosine, which is
 * exactly the fallback `$lib/tidegauge` already uses when its live series runs
 * out. Standard practice, and the rule of twelfths is the same curve done by
 * hand.
 */

export interface TideEventPt {
  t: number;
  /** High or low water. */
  kind: 'H' | 'L';
  /** Metres on the station's own datum. Only differences are used. */
  m: number;
}

export interface TideStation {
  code: string;
  name: string;
  lat: number;
  lon: number;
  /** Synthesised from CHS table corrections rather than published directly. */
  derived: boolean;
  events: TideEventPt[];
}

/** Feet, because every tide reading in this app is feet (the anchor gauge, the
 * environment card, the legacy dash). CHS publishes metres, which is why both
 * are shown beside each other in the sheet. */
export const FT = 3.28084;

/** How far ahead the sheet's "next ≥ X ft" finder searches. Matches the
 * scrubber's +48 h ceiling — a tide further out than the scrub could ever reach
 * is not an answer the popup is built to give. */
export const NEXT_TIDE_LOOKAHEAD_H = 48;

export interface PackedTides {
  year: number;
  epochUtcMs: number;
  stations: {
    code: string;
    name: string;
    lat: number;
    lon: number;
    derived: boolean;
    dt: number[];
    kind: string;
    hCm: number[];
  }[];
}

export function unpackTides(p: PackedTides): TideStation[] {
  return p.stations.map((s) => {
    const events: TideEventPt[] = [];
    let mins = 0;
    for (let i = 0; i < s.dt.length; i++) {
      mins += s.dt[i]!;
      events.push({
        t: p.epochUtcMs + mins * 60_000,
        kind: s.kind[i] === 'H' ? 'H' : 'L',
        m: (s.hCm[i] ?? 0) / 100
      });
    }
    return { ...s, events };
  });
}

function before(events: TideEventPt[], t: number): number {
  let lo = 0;
  let hi = events.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (events[mid]!.t <= t) {
      ans = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return ans;
}

/** Half-cosine height between two adjacent events at `t`. The same curve
 * `tideAt` draws — shared so the finder and the readout can never disagree. */
function heightBetween(a: TideEventPt, b: TideEventPt, t: number): number {
  const span = b.t - a.t;
  const u = span > 0 ? (t - a.t) / span : 1;
  const s = (1 - Math.cos(Math.PI * u)) / 2;
  return a.m + (b.m - a.m) * s;
}

export interface TideNowAt {
  /** Height on the station datum. Meaningful only against `low`/`high`. */
  m: number;
  low: number;
  high: number;
  /** 0 at this cycle's low water, 1 at its high. What the bar draws. */
  frac: number;
  rising: boolean;
  /** The event being climbed to or fallen to. */
  next: TideEventPt;
  /** Metres per hour, signed. Zero at the turn. */
  ratePerHour: number;
}

/**
 * The tide at `t`, interpolated between the shipped events.
 *
 * Null outside the covered year rather than extrapolated — a height past the
 * end of the data is a fabrication, and people take ground on these numbers.
 */
export function tideAt(st: TideStation, t: number): TideNowAt | null {
  const ev = st.events;
  if (ev.length < 2) return null;
  const i = before(ev, t);
  if (i < 0 || i >= ev.length - 1) return null;

  const a = ev[i]!;
  const b = ev[i + 1]!;
  const span = b.t - a.t;
  const u = span > 0 ? (t - a.t) / span : 1;
  // Half-cosine from a to b: flat at both turns, fastest in the middle.
  const m = heightBetween(a, b, t);

  const low = Math.min(a.m, b.m);
  const high = Math.max(a.m, b.m);
  const rising = b.m > a.m;
  /* Guard the flat cycle: a high and low of equal height would divide by zero
   * and put the marker at NaN, which draws as nothing at all. */
  const frac = high - low > 1e-6 ? (m - low) / (high - low) : 0.5;

  // d/dt of the cosine, in metres per hour.
  const rate =
    span > 0 ? ((b.m - a.m) * Math.PI * Math.sin(Math.PI * u)) / 2 / (span / 3_600_000) : 0;

  return { m, low, high, frac, rising, next: b, ratePerHour: rate };
}

export interface NextTideAtLeast {
  /** Epoch ms of the first instant at or after `fromT` at or above the threshold. */
  t: number;
  /** Metres on the station's datum — the height `tideAt` would read there. */
  m: number;
}

/** Inverse of the half-cosine: the fraction of a segment at which the tide
 * reaches a given share of the rise or fall. `c` is in [0, 1]. */
const cosShare = (c: number): number => Math.acos(1 - 2 * c) / Math.PI;

/**
 * The first `t >= fromT`, within the +48 h lookahead, at which the interpolated
 * height reaches `ft` (feet). If the height is already there at `fromT`, returns
 * `fromT` itself. Null when no such instant exists in scope — past the end of
 * the data, or simply never that high.
 *
 * The sheet's "NEXT ≥ X FT" finder. It walks the event segments rather than
 * sampling, so the crossing is solved exactly on the same half-cosine `tideAt`
 * draws instead of landing between two 20-minute samples.
 */
export function nextTimeHeightAtLeast(
  st: TideStation,
  fromT: number,
  ft: number
): NextTideAtLeast | null {
  const ev = st.events;
  if (ev.length < 2) return null;
  const end = fromT + NEXT_TIDE_LOOKAHEAD_H * 3_600_000;
  const thresh = ft / FT;
  let best: NextTideAtLeast | null = null;
  for (let i = 0; i < ev.length - 1; i++) {
    const a = ev[i]!;
    if (a.t > end) break;
    const b = ev[i + 1]!;
    if (b.t < fromT) continue;
    const span = b.t - a.t;
    if (span <= 0) continue;

    /* Between a and b the height moves monotonically along the half-cosine, so
     * the stretch where it is at or above the threshold is one contiguous run:
     * the far end of a rising segment, the near end of a falling one. `q0`/`q1`
     * bracket that run in segment time. */
    const lo = Math.min(a.m, b.m);
    const hi = Math.max(a.m, b.m);
    if (hi < thresh) continue; // never reaches it in this stretch
    let q0: number;
    let q1: number;
    if (b.m > a.m) {
      q0 = lo >= thresh ? a.t : a.t + cosShare((thresh - a.m) / (b.m - a.m)) * span;
      q1 = b.t;
    } else if (b.m < a.m) {
      if (lo >= thresh) {
        q0 = a.t;
        q1 = b.t;
      } else {
        q0 = a.t;
        q1 = a.t + cosShare((thresh - a.m) / (b.m - a.m)) * span;
      }
    } else {
      q0 = a.t;
      q1 = b.t;
    }

    const cand = Math.max(q0, fromT);
    if (cand > Math.min(q1, end)) continue;
    if (!best || cand < best.t) best = { t: cand, m: heightBetween(a, b, cand) };
    if (best.t <= fromT) break; // already at or above threshold — cannot do better
  }
  return best;
}

/** Stations within `km`, nearest first. */
export function nearTideStations(
  stations: TideStation[],
  pos: { lat: number; lon: number },
  km: number
): TideStation[] {
  const R = 111.32;
  const cos = Math.cos((pos.lat * Math.PI) / 180);
  return stations
    .map((s) => ({ s, d: Math.hypot((s.lat - pos.lat) * R, (s.lon - pos.lon) * R * cos) }))
    .filter((x) => x.d <= km)
    .sort((a, b) => a.d - b.d)
    .map((x) => x.s);
}

/* The bar has eight steps, not a continuous fill.
 *
 * One icon per step, because MapLibre needs a raster image per symbol and a
 * continuous one would mean an image per station per frame. Eight is enough to
 * read "nearly high", "about half", "nearly low" at a glance, which is the
 * whole job of the symbol — the number underneath carries the precision. */
export const TIDE_STEPS = 8;
export const tideStep = (frac: number): number =>
  Math.max(0, Math.min(TIDE_STEPS - 1, Math.round(frac * (TIDE_STEPS - 1))));
